<template>
  <div class="autogenerated-wrap">
    <div class="buttons-wrap">
      <or-button
        v-if="showActionButton"
        class="mr-16 no-border custom-spacing"
        type="secondary"
        color="red"
        :loading="isDeleting"
        :disabled="isAccepting"
        @click="$refs.deleteConfirm.open()"
      >
        {{ $t('generic.delete') }}
      </or-button>
      <or-button
        v-if="showActionButton"
        class="no-border custom-spacing"
        type="secondary"
        color="primary"
        :loading="isAccepting"
        :disabled="isDeleting"
        @click="acceptButtonHandler"
      >
        {{ $t('generic.accept') }}
      </or-button>
    </div>
    <TabPlaceholder
      v-if="notFound"
      :caption="$t('generic.oopsNotFound')"
      :emoji="notFoundEmoji"
      :info="$t('generic.oopsNotFoundPhraseInfo', { phrase: searchText })"
    />
    <TabPlaceholder
      v-else-if="tableIsEmpty"
      :caption="placeholderCaption"
    />
    <div v-else class="table-wrap">
      <div class="table">
        <Header
          :group-checkbox-style="groupCheckboxStyle"
          :group-is-checked="groupIsChecked"
          @checkbox-group-toggle="toggleCheckboxGroup"
        />
        <div
          class="content-wrap scroll"
          v-infinite-scroll="loadMore"
          infinite-scroll-disabled="autogeneratedIsLoading"
          infinite-scroll-distance="200"
        >
          <AutogeneratedRow
            v-for="utterance in tableData"
            :utterance="utterance"
            :key="utterance.id"
            :checked-set="checkedSet"
            :is-accepting="isAccepting"
            :is-deleting="isDeleting"
          />
        </div>
        <or-progress-linear v-show="autogeneratedIsLoading" color="primary" />
      </div>
    </div>
    <or-confirm
      ref="deleteConfirm"
      :title="deleteConfirmTitle"
      @confirm="deleteConfirmHandler"
    >
      {{ $t('autogenerated.autogenerated_table.deleteModal.description') }}
    </or-confirm>
    <or-confirm
      ref="acceptConfirm"
      :title="acceptConfirmTitle"
      @confirm="acceptConfirmHandler"
    >
      {{ $t('autogenerated.autogenerated_table.acceptModal.description') }}
    </or-confirm>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapActions } from 'vuex'
import infiniteScroll from 'vue-infinite-scroll'
import { autogeneratedValidationMixin } from '@/mixins/autogenerated-validation.mixin'
import notificationEmojies from '@/modules/notification-emojies.mapping'
import KnowledgeBus from '@/KnowledgeBus'
import TabPlaceholder from '@/components/common/TabPlaceholder'
import Header from './autogenerated-table-header'
import AutogeneratedRow from './autogenerated-row'

export default {
  components: {
    TabPlaceholder,
    Header,
    AutogeneratedRow
  },

  directives: { infiniteScroll },

  mixins: [autogeneratedValidationMixin],

  created () {
    KnowledgeBus.$on('row-checkbox::toggle', this.toggleCheckboxRow)
  },

  beforeDestroy () {
    KnowledgeBus.$off('row-checkbox::toggle', this.toggleCheckboxRow)
  },

  props: {
    autogeneratedType: String,
    searchText: String
  },

  computed: {
    ...mapState('knowledgebase', ['current']),
    ...mapState('knowledgebase/autogenerated', ['suggested', 'unrecognized']),

    notFoundEmoji () {
      return _.get(notificationEmojies, 'notFound', '')
    },

    placeholderCaption () {
      return this.$t('autogenerated.autogenerated_table.emptyTable', { autogeneratedType: this.$props.autogeneratedType })
    },

    autogeneratedIsLoading () {
      return this.unrecognized.loading || this.suggested.loading
    },

    deleteConfirmTitle () {
      return this.$t('autogenerated.autogenerated_table.deleteModal.title', { autogeneratedType: this.$props.autogeneratedType })
    },

    acceptConfirmTitle () {
      return this.$t('autogenerated.autogenerated_table.acceptModal.title', { autogeneratedType: this.$props.autogeneratedType })
    },

    tableData () {
      return _.get(this, `${this.$props.autogeneratedType}.value`)
    },

    valuesIds () {
      return _.map(this.tableData, 'id')
    },

    tableIsEmpty () {
      return (
        _.isEmpty(this.tableData) &&
        _.isEmpty(this.$props.searchText) &&
        !this.autogeneratedIsLoading
      )
    },

    notFound () {
      return (
        _.isEmpty(this.tableData) &&
        !_.isEmpty(this.$props.searchText) &&
        !this.autogeneratedIsLoading
      )
    },

    showActionButton () {
      return !_.isEmpty(this.dataToAct)
    },

    dataToAct () {
      return _.filter(this.tableData, value =>
        _.includes(this.checkedSet, value.id)
      )
    },

    dataIsAllowedToAdd () {
      return !this.$v.dataToAct.$invalid
    },

    groupCheckboxStyle () {
      const middleState = _.some(this.valuesIds, id =>
        _.includes(this.checkedSet, id)
      )

      return this.groupIsChecked ? 'checked' : middleState ? 'middlestate' : ''
    },

    groupIsChecked () {
      return _.every(this.valuesIds, id => _.includes(this.checkedSet, id))
    }
  },

  data () {
    return {
      checkedSet: [],
      isAccepting: false,
      isDeleting: false
    }
  },

  methods: {
    ...mapActions('knowledgebase', ['fetchCategories']),
    ...mapActions('knowledgebase/autogenerated', [
      'fetchAutogenerated',
      'batchDeleteAutogenerated',
      'batchApproveAutogenerated'
    ]),
    ...mapActions('knowledgebase/notifications', ['addWarning']),
    ...mapActions('knowledgebase/statuses', ['fetchStatus']),

    loadMore () {
      if (!this.isDeleting && !this.isAccepting) {
        this.fetchAutogenerated({
          type: this.$props.autogeneratedType,
          searchText: this.$props.searchText
        })
      }
    },

    acceptButtonHandler () {
      this.dataIsAllowedToAdd
        ? this.$refs.acceptConfirm.open()
        : this.addWarning({ titleLabel: 'autogenerated.autogenerated_table.allFieldsRequired' })
    },

    async acceptConfirmHandler () {
      this.isAccepting = true
      await this.batchApproveAutogenerated({
        type: this.autogeneratedType,
        utterances: this.dataToAct,
        searchText: this.searchText
      })
      this.isAccepting = false
      this.checkedSet = []
      this.fetchCategories()
      this.fetchStatus()
    },

    async deleteConfirmHandler () {
      this.isDeleting = true
      const ids = _.map(this.dataToAct, 'id')
      await this.batchDeleteAutogenerated({
        ids,
        type: this.autogeneratedType,
        searchText: this.searchText
      })
      this.isDeleting = false
      this.checkedSet = []
    },

    toggleCheckboxGroup () {
      if (_.every(this.valuesIds, id => _.includes(this.checkedSet, id))) {
        this.checkedSet = _.filter(
          this.checkedSet,
          item => !_.includes(this.valuesIds, item)
        )
      } else {
        _.forEach(this.valuesIds, id => {
          if (!_.includes(this.checkedSet, id)) {
            this.checkedSet.push(id)
          }
        })
      }
    },

    toggleCheckboxRow (id) {
      this.checkedSet = _.includes(this.checkedSet, id)
        ? _.filter(this.checkedSet, n => n !== id)
        : [...this.checkedSet, id]
    }
  },

  watch: {
    autogeneratedType (value) {
      this.loadMore()
    }
  }
}
</script>

<style lang="scss">
.mr-16 {
  margin-right: $sp-3;
}

.custom-spacing.ui-button {
  padding: 4px 0;
  margin: 0;
}

.autogenerated-wrap {
  display: flex;
  flex-direction: column;
  height: 100%;

  .buttons-wrap {
    display: flex;
    justify-content: flex-end;
    min-height: 32px;

    .no-border {
      border: none;
    }
  }

  .table-wrap {
    display: flex;
    flex-direction: column;
    position: relative;
    height: calc(100% - 32px);

    .table {
      display: flex;
      flex-direction: column;
      overflow: hidden;

      .content-wrap {
        position: relative;
        height: 100%;
        overflow-x: scroll;
        overflow: auto;

        .row {
          border-left: solid 1px #dfdfdf;
          align-items: stretch;
        }
      }

      .flex {
        display: flex;
        align-items: stretch;
        flex-grow: 1;

        &-column {
          flex-direction: column;
          flex-grow: 1;
        }
      }

      .w {
        &-80 {
          width: 80%;

          .w-50.header:first-child {
            border-top-left-radius: 4px;
          }
        }

        &-20 {
          width: 20%;

          &.header {
            border-top-right-radius: 4px;
          }
        }

        &-50 {
          width: 50%;

          &.header {
            display: flex;

            &:first-child {
              border-right: none;
            }
          }

          &.utterance {
            border-right: solid 1px #dfdfdf;
          }
        }
      }

      .row {
        display: flex;
        align-items: center;
        min-height: 32px;
        background-color: $or-white;

        .loader {
          display: flex;
          align-items: center;
          margin: 0 16px;
          flex-grow: 1;
        }

        .col {
          padding: 4px;
          width: 25%;

          & > .ui-autocomplete {
            margin: 0;
            &
              .ui-autocomplete__content
              .ui-autocomplete__label
              .ui-autocomplete__input {
              border: none;
              background-color: transparent;
            }
          }

          &:first-child {
            width: 36px;
          }

          &:last-child {
            width: 56px;
          }

          &:nth-child(2) {
            width: 50%;
          }
        }
      }
    }

    &__footer {
      display: flex;
      justify-content: flex-end;
      height: 32px;
      margin-top: 32px;
    }
  }
}
</style>
